import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Plus, Edit, Trash2, Eye, EyeOff, ChevronLeft, ChevronRight,
  Settings, Upload, AlertTriangle, UserPlus, User, List, X, Phone, Mail,
  Share2, Copy, Link, RotateCcw, ExternalLink, Download, QrCode, Ticket,
  Users, CheckCircle, XCircle, Wine, MessageCircle, RefreshCw
} from "lucide-react";
import { API, BACKEND_URL } from "@/config/api";
import PasswordInput from "@/components/common/PasswordInput";

const AdminUsersTab = ({ credentials, events, fetchEvents, isGestor, gestorPrivileges, isSuperuser, setActiveTab }) => {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [superuserConfig, setSuperuserConfig] = useState({ username: "", nombre: "", email: "" });
  const [adminUsers, setAdminUsers] = useState([]);
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [editingAdminUser, setEditingAdminUser] = useState(null);
  const [adminForm, setAdminForm] = useState({
    username: "",
    password: "",
    nombre: "",
    email: "",
    activo: true
  });

  const fetchAdminUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/admin-users`, {
        headers: { Authorization: `Basic ${credentials}` }
      });
      setAdminUsers(response.data);
    } catch (e) {
      console.error("Error fetching admin users:", e);
    }
  }, [credentials]);

  useEffect(() => {
    fetchAdminUsers();
  }, [fetchAdminUsers]);

  useEffect(() => {
    const fetchSuperuser = async () => {
      try {
        const response = await axios.get(`${API}/admin/superuser`, {
          headers: { Authorization: `Basic ${credentials}` }
        });
        setSuperuserConfig({
          username: response.data.username || "",
          nombre: response.data.nombre || "",
          email: response.data.email || ""
        });
      } catch (e) {
        console.error("Error fetching superuser:", e);
      }
    };
    fetchSuperuser();
  }, [credentials]);

  return (
    <>
          <>
            {/* Add Admin Button */}
            {!showAdminForm && (
              <button 
                onClick={() => {
                  setEditingAdminUser(null);
                  setAdminForm({ username: "", password: "", nombre: "", email: "", activo: true });
                  setShowAdminForm(true);
                }}
                className="btn-primary mb-8 w-auto px-6"
                data-testid="add-admin-btn"
              >
                <UserPlus size={18} className="inline mr-2" />
                Nuevo Administrador
              </button>
            )}

            {/* Admin Form */}
            {showAdminForm && (
              <div className="form-section mb-8 max-w-md">
                <h3 className="form-title mb-6">
                  {editingAdminUser ? "Editar Administrador" : "Nuevo Administrador"}
                </h3>
                
                {error && <div className="error-message mb-4">{error}</div>}
                
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  setLoading(true);
                  setError("");
                  try {
                    const config = { headers: { Authorization: `Basic ${credentials}` } };
                    
                    if (editingAdminUser) {
                      const updateData = {
                        username: adminForm.username,
                        nombre: adminForm.nombre,
                        email: adminForm.email,
                        activo: adminForm.activo
                      };
                      if (adminForm.password) {
                        updateData.password = adminForm.password;
                      }
                      await axios.put(`${API}/admin/admin-users/${editingAdminUser.id}`, updateData, config);
                    } else {
                      await axios.post(`${API}/admin/admin-users`, adminForm, config);
                    }
                    
                    setShowAdminForm(false);
                    setEditingAdminUser(null);
                    setAdminForm({ username: "", password: "", nombre: "", email: "", activo: true });
                    fetchAdminUsers();
                  } catch (err) {
                    setError(err.response?.data?.detail || "Error al guardar administrador");
                  } finally {
                    setLoading(false);
                  }
                }}>
                  <div className="form-group">
                    <label className="form-label">Nombre de Usuario</label>
                    <input
                      type="text"
                      className="custom-input"
                      value={adminForm.username}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, username: e.target.value }))}
                      required
                      data-testid="admin-form-username"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      {editingAdminUser ? "Nueva Contraseña (dejar vacío para mantener)" : "Contraseña"}
                    </label>
                    <PasswordInput
                      value={adminForm.password}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, password: e.target.value }))}
                      required={!editingAdminUser}
                      minLength={6}
                      testId="admin-form-password"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Nombre Completo</label>
                    <input
                      type="text"
                      className="custom-input"
                      value={adminForm.nombre}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, nombre: e.target.value }))}
                      required
                      data-testid="admin-form-nombre"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="custom-input"
                      placeholder="admin@ejemplo.com"
                      value={adminForm.email}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, email: e.target.value }))}
                      data-testid="admin-form-email"
                    />
                  </div>
                  
                  {editingAdminUser && (
                    <div className="form-group">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={adminForm.activo}
                          onChange={(e) => setAdminForm(prev => ({ ...prev, activo: e.target.checked }))}
                          className="w-5 h-5 accent-[var(--primary)]"
                          data-testid="admin-form-activo"
                        />
                        <span className="text-white">Usuario Activo</span>
                      </label>
                      <p className="text-xs text-[var(--text-muted)] mt-1">
                        Los administradores inactivos no pueden acceder al panel
                      </p>
                    </div>
                  )}
                  
                  <div className="flex gap-4 mt-6">
                    <button type="submit" className="btn-primary" disabled={loading} data-testid="admin-submit-btn">
                      {loading ? <span className="spinner"></span> : (editingAdminUser ? "Guardar Cambios" : "Crear Administrador")}
                    </button>
                    <button 
                      type="button" 
                      onClick={() => {
                        setShowAdminForm(false);
                        setEditingAdminUser(null);
                        setAdminForm({ username: "", password: "", nombre: "", email: "", activo: true });
                        setError("");
                      }}
                      className="btn-download"
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Admins List */}
            <div className="admin-events-list">
              <h3 className="font-heading text-xl text-white mb-4 uppercase">
                Administradores ({adminUsers.length + 1})
              </h3>
              
              <div className="space-y-4">
                {/* Main Admin (Superuser) - editable from Config tab */}
                <div className="admin-event-card border-l-4 border-[var(--primary)]">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[var(--primary)] flex items-center justify-center">
                        <Settings size={24} className="text-black" />
                      </div>
                      <div>
                        <h4 className="text-white font-bold">
                          {superuserConfig.nombre || "Administrador Principal"}
                        </h4>
                        <p className="text-[var(--text-muted)] text-sm">@{superuserConfig.username || 'TAYTANTOS'}</p>
                        {superuserConfig.email && (
                          <p className="text-[var(--accent-cyan)] text-xs">{superuserConfig.email}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setActiveTab("config")}
                        className="p-2 text-[var(--text-muted)] hover:text-[var(--primary)] transition-colors"
                        title="Editar en Configuración"
                        data-testid="edit-superuser-btn"
                      >
                        <Edit size={18} />
                      </button>
                      <span className="text-xs px-3 py-1 bg-[var(--primary)] text-black font-bold">
                        SUPERUSUARIO
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Additional Admins */}
                {adminUsers.map(admin => (
                  <div 
                    key={admin.id} 
                    className={`admin-event-card ${admin.activo === false ? 'opacity-50' : ''}`}
                    data-testid={`admin-user-${admin.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 ${admin.activo === false ? 'bg-gray-600' : 'bg-[var(--accent-cyan)]'} flex items-center justify-center`}>
                          <User size={24} className="text-black" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="text-white font-bold">{admin.nombre}</h4>
                            {admin.activo === false && (
                              <span className="text-xs px-2 py-0.5 bg-gray-600 text-gray-300">INACTIVO</span>
                            )}
                          </div>
                          <p className="text-[var(--text-muted)] text-sm">@{admin.username}</p>
                          {admin.email && (
                            <p className="text-[var(--accent-cyan)] text-xs">{admin.email}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => {
                            setEditingAdminUser(admin);
                            setAdminForm({
                              username: admin.username,
                              password: "",
                              nombre: admin.nombre,
                              email: admin.email || "",
                              activo: admin.activo !== false
                            });
                            setShowAdminForm(true);
                          }}
                          className="p-2 text-[var(--text-muted)] hover:text-[var(--primary)] transition-colors"
                          title="Editar administrador"
                          data-testid={`edit-admin-${admin.id}`}
                        >
                          <Edit size={18} />
                        </button>
                        <button 
                          onClick={async () => {
                            if (window.confirm(`¿Eliminar al administrador ${admin.nombre}?`)) {
                              try {
                                await axios.delete(`${API}/admin/admin-users/${admin.id}`, {
                                  headers: { Authorization: `Basic ${credentials}` }
                                });
                                fetchAdminUsers();
                              } catch (err) {
                                setError("Error al eliminar administrador");
                              }
                            }
                          }}
                          className="p-2 text-[var(--text-muted)] hover:text-[var(--error)] transition-colors"
                          data-testid={`delete-admin-${admin.id}`}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>

    </>
  );
};

export default AdminUsersTab;
